import React, { useContext } from 'react';
import { useMutation, MutationFunction } from '@apollo/client';
import { clone, equals } from 'ramda';
import Logger from '../../../../../../../../../Utils/Logger';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import {
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../../../EkonomiskAnalysPage';
import { ActiveDatabaseContext } from '../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { isSameAnalys } from '../Components/FavoriteAnalysDialog/Utils/EkonomiskAnalysFavoriteAnalysDialogUtils';
import addToRecentAnalyserMutation from './AddToRecentAnalyserMutation.gql';
import EkonomiskAnalysInitQuery from '../../../../Queries/EkonomiskAnalysInitQuery/AnalysInitQuery.gql';
import { paddedEkonomiskAnalysRutinId } from '../Components/FavoriteAnalysDialog/Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import {
  AddToRecentAnalyserMutation as IAddToRecentAnalyserMutationData,
  AddToRecentAnalyserMutationVariables as IAddToRecentAnalyserMutationVariables,
} from './_QueryTypes/AddToRecentAnalyserMutation';
import { NEW_ANALYS_RAPPORT_ID } from '../../Components/CreateNewAnalys/CreateNewAnalysUtils';

interface IProps {
  recentAnalyser: IRecentAnalysInfo[];
  children: (mutateFn: AddToRecentAnalyserFnType) => React.ReactNode;
}

export interface IRecentAnalysInfo {
  analysName: string;
  analysId: string;
  analysIdKod?: string;
  kodbehId: string;
  kodbehName: string;
  utdelningLopnr: number | null;
  baseSelectionName?: string;
  baseSelectionId?: string;
  category?: string;
  categoryID?: number;
  ai_prompt?: string | null;
  detaljanalys?: boolean;
}

export type AddToRecentAnalyserFnType = (
  newAnalys: ISelectedAnalysAnalys,
  newCodePrivilege: ISelectedAnalysCodePrivilege,
  currentRecentAnalyser: IRecentAnalysInfo[]
) => void;

const getRecentAnalyser = (
  newAnalys: IRecentAnalysInfo,
  recentAnalyser: IRecentAnalysInfo[]
): IRecentAnalysInfo[] => {
  const newRecentAnalyser = clone(recentAnalyser);
  const maxnewRecentAnalyser = 5;
  const allreadyExist = newRecentAnalyser.findIndex(analys =>
    isSameAnalys(
      {
        rapportId: analys.analysId,
        utdelningLopnr: analys.utdelningLopnr,
        kodbehorighet: analys.kodbehId,
        idKod: undefined,
      },
      {
        rapportId: newAnalys.analysId,
        utdelningLopnr: newAnalys.utdelningLopnr,
        kodbehorighet: newAnalys.kodbehId,
        idKod: undefined,
      }
    )
  );
  if (allreadyExist !== -1) {
    newRecentAnalyser[allreadyExist] = newAnalys;
    return newRecentAnalyser;
  } else {
    return [newAnalys, ...newRecentAnalyser].slice(0, maxnewRecentAnalyser);
  }
};

const addToRecentAnalyser =
  (
    activeDatabase: string | null,
    addToRecentAnalyserMutationFn: MutationFunction<
      IAddToRecentAnalyserMutationData,
      IAddToRecentAnalyserMutationVariables
    >,
    codeplanID: string | undefined
  ): AddToRecentAnalyserFnType =>
  (newAnalys, newCodePrivilege, currentRecentAnalyser) => {
    if (activeDatabase) {
      const analys: IRecentAnalysInfo = {
        analysName: newAnalys.name,
        analysId: newAnalys.id,
        kodbehId: newCodePrivilege.id,
        kodbehName: newCodePrivilege.name,
        utdelningLopnr: newAnalys.utdelningLopnr,
        baseSelectionName: newAnalys.grundurval
          ? newAnalys.grundurval.name
          : undefined,
        baseSelectionId: newAnalys.grundurval
          ? newAnalys.grundurval.id
          : undefined,
        category: newAnalys.category,
        categoryID: newAnalys.categoryID,
        ai_prompt: newAnalys.ai_prompt,
        detaljanalys: newAnalys.detaljanalys,
      };
      const newRecent: IRecentAnalysInfo[] = getRecentAnalyser(
        analys,
        currentRecentAnalyser
      ).filter(a => a.analysId !== NEW_ANALYS_RAPPORT_ID);

      if (!equals(currentRecentAnalyser, newRecent)) {
        addToRecentAnalyserMutationFn({
          variables: {
            dbname: activeDatabase,
            recentAnalyser: JSON.stringify(newRecent),
          },
          refetchQueries: result => {
            if (result && result.data && result.errors == null && codeplanID) {
              return [
                {
                  query: EkonomiskAnalysInitQuery,
                  variables: {
                    codeSystemId: codeplanID,
                    dbName: activeDatabase || '',
                    onlyFavorites: false,
                    section: [`Forms/${paddedEkonomiskAnalysRutinId}`],
                    subsystemCode: 'EK',
                  },
                },
              ];
            } else {
              return [];
            }
          },
        });
      }
    } else {
      Logger.error(
        Error(
          `Kunde ej ändra senaste analyser, activeDatabase: ${activeDatabase}`
        )
      );
    }
  };

const AddToRecentAnalyserMutation: React.FunctionComponent<IProps> = props => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const [addToRecentAnalyserMutationFn] = useMutation<
    IAddToRecentAnalyserMutationData,
    IAddToRecentAnalyserMutationVariables
  >(addToRecentAnalyserMutation);
  const kodplanContext = useContext(KodplanContext);
  return (
    <>
      {props.children(
        addToRecentAnalyser(
          activeDatabaseContext.activeDatabase,
          addToRecentAnalyserMutationFn,
          kodplanContext.activeKodplan?.id
        )
      )}
    </>
  );
};

export default AddToRecentAnalyserMutation;
